import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getSessionSlots, upsertSessionSlot, deleteSessionSlot, SLOT_COURSES } from '../../lib/api';
import type { SessionSlot, SlotCourse } from '../../lib/api';

const isSlotCourse = (c?: string): c is SlotCourse => SLOT_COURSES.some(x => x.value === c);

// Which students see the time: the form asks online or offline first
const CATEGORY_LABELS: Record<SessionSlot['sessionCategory'], string> = {
  both: 'Online & Offline', online: 'Online only', offline: 'Offline only',
};

// ── Add / Edit Modal ─────────────────────────────────────────────────────────

const SlotModal: React.FC<{
  slot: SessionSlot | null;
  initialCourse: SlotCourse | '';             // course of the section "+ Add time" was clicked in
  adminPassword: string;
  nextOrder: (course: SlotCourse) => number;
  onClose: () => void;
  onDone: () => void;
}> = ({ slot, initialCourse, adminPassword, nextOrder, onClose, onDone }) => {
  const [label, setLabel] = useState(slot?.label ?? '');
  const [course, setCourse] = useState<SlotCourse | ''>(isSlotCourse(slot?.course) ? slot!.course as SlotCourse : initialCourse);
  const [category, setCategory] = useState<SessionSlot['sessionCategory']>(slot?.sessionCategory ?? 'both');
  const [available, setAvailable] = useState(slot?.available ?? true);
  const [order, setOrder] = useState(slot?.order ?? (initialCourse ? nextOrder(initialCourse) : 0));
  const [saving, setSaving] = useState(false);

  const pickCourse = (c: SlotCourse) => {
    setCourse(c);
    if (!slot) setOrder(nextOrder(c));   // a new slot goes to the end of that course's list
  };

  const handleSave = async () => {
    if (!label.trim()) { toast.error('Please enter the time for this slot'); return; }
    if (!course) { toast.error('Pick the course this time is for'); return; }
    setSaving(true);
    try {
      await upsertSessionSlot(adminPassword, {
        slotId: slot?._id, label: label.trim(), course, sessionCategory: category, available, order: Number(order) || 0,
      });
      toast.success(slot ? 'Slot updated' : 'Slot added');
      onDone();
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm z-10 overflow-hidden">
        <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
          <p className="text-white font-bold text-base">{slot ? 'Edit Slot' : 'Add Time Slot'}</p>
          <p className="text-white/60 text-xs mt-0.5">Shown to students who pick this course on the group-session form</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Course *</label>
            <div className="grid grid-cols-2 gap-2">
              {SLOT_COURSES.map(c => (
                <button key={c.value} type="button" onClick={() => pickCourse(c.value)}
                  className="py-2 rounded-xl text-xs font-bold border-2 transition-all"
                  style={course === c.value
                    ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                    : { background: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb' }}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Time *</label>
            <input value={label} onChange={e => setLabel(e.target.value)} autoFocus
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400"
              placeholder="e.g. Morning 9.30 am" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">For students who chose</label>
            <div className="grid grid-cols-3 gap-2">
              {(['both', 'online', 'offline'] as const).map(c => (
                <button key={c} type="button" onClick={() => setCategory(c)}
                  className="py-2 rounded-xl text-[11px] font-bold border-2 transition-all"
                  style={category === c
                    ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                    : { background: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb' }}>
                  {CATEGORY_LABELS[c]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Display Order</label>
            <input type="number" value={order} onChange={e => setOrder(Number(e.target.value))}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none p-3 rounded-xl bg-purple-50 border border-purple-100">
            <input type="checkbox" checked={available} onChange={e => setAvailable(e.target.checked)}
              className="w-4 h-4 rounded accent-purple-700" />
            <span className="text-xs text-gray-700">
              Available — <span className="font-semibold text-purple-700">visible to students right now</span>
            </span>
          </label>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
              {saving ? 'Saving…' : slot ? 'Save Changes' : 'Add Slot'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── One course's slots ────────────────────────────────────────────────────────

const SlotTable: React.FC<{
  slots: SessionSlot[];
  deleting: string | null;
  onToggle: (s: SessionSlot) => void;
  onEdit: (s: SessionSlot) => void;
  onDelete: (s: SessionSlot) => void;
}> = ({ slots, deleting, onToggle, onEdit, onDelete }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-gray-50">
          {['Order', 'Time', 'For', 'Status', ''].map(h => (
            <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase whitespace-nowrap tracking-wide">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {slots.map(s => (
          <tr key={s._id} className="border-t border-gray-100 hover:bg-purple-50/30 transition-colors">
            <td className="px-4 py-3.5 text-xs text-gray-400 font-medium w-16">{s.order}</td>
            <td className="px-4 py-3.5 font-semibold text-gray-800">{s.label}</td>
            <td className="px-4 py-3.5 text-xs text-gray-500 whitespace-nowrap">{CATEGORY_LABELS[s.sessionCategory] ?? CATEGORY_LABELS.both}</td>
            <td className="px-4 py-3.5">
              <button onClick={() => onToggle(s)}
                className="text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors whitespace-nowrap"
                style={s.available ? { background: '#dcfce7', color: '#15803d' } : { background: '#f3f4f6', color: '#9ca3af' }}>
                {s.available ? '✓ Available' : 'Hidden'}
              </button>
            </td>
            <td className="px-4 py-3.5">
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => onEdit(s)}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 whitespace-nowrap"
                  style={{ borderColor: '#3b82f6', color: '#1d4ed8' }}>
                  Edit
                </button>
                <button onClick={() => onDelete(s)} disabled={deleting === s._id}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 whitespace-nowrap disabled:opacity-50"
                  style={{ borderColor: '#ef4444', color: '#b91c1c' }}>
                  {deleting === s._id ? '…' : 'Delete'}
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const SessionSlots: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [slots, setSlots] = useState<SessionSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ slot: SessionSlot | null; course: SlotCourse | '' } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSessionSlots(adminPassword);
      setSlots(res.slots ?? []);
    } catch (err: any) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const toggleAvailable = async (slot: SessionSlot) => {
    try {
      await upsertSessionSlot(adminPassword, {
        slotId: slot._id, label: slot.label, course: isSlotCourse(slot.course) ? slot.course : '',
        sessionCategory: slot.sessionCategory, available: !slot.available, order: slot.order,
      });
      setSlots(prev => prev.map(s => s._id === slot._id ? { ...s, available: !s.available } : s));
      toast.success(`${slot.label} is now ${!slot.available ? 'available' : 'hidden'}`);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleDelete = async (slot: SessionSlot) => {
    setDeleting(slot._id);
    try {
      await deleteSessionSlot(adminPassword, slot._id);
      setSlots(prev => prev.filter(s => s._id !== slot._id));
      toast.success(`${slot.label} removed`);
    } catch (err: any) { toast.error(err.message); }
    finally { setDeleting(null); }
  };

  const slotsFor = (course: SlotCourse) => slots.filter(s => s.course === course);
  const nextOrder = (course: SlotCourse) => slotsFor(course).length;
  // Added before each course had its own times — the form shows these for every course
  const noCourse = slots.filter(s => !isSlotCourse(s.course));
  const tableProps = {
    deleting,
    onToggle: toggleAvailable,
    onEdit: (s: SessionSlot) => setModal({ slot: s, course: '' }),
    onDelete: handleDelete,
  };

  return (
    <TrackerLayout>
      <div className="space-y-5">
        <div className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">🕐</span>
                <h1 className="text-xl font-bold text-white">Session Slots</h1>
              </div>
              <p className="text-white/50 text-xs">Each course has its own times. On the Pay After Placement group-session form, a student sees only the Available slots for the course and the mode (online / offline) they picked.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setModal({ slot: null, course: '' })}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90 whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>
                + Add Slot
              </button>
              <button onClick={load}
                className="px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors whitespace-nowrap">
                ↻ Refresh
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 rounded-full border-3 border-purple-200 border-t-purple-600 animate-spin" style={{ borderWidth: 3 }} />
            <p className="text-sm text-gray-400">Loading slots…</p>
          </div>
        ) : (
          <>
            {noCourse.length > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 overflow-hidden">
                <div className="px-5 py-4">
                  <p className="font-bold text-amber-900">No course set — shown for both courses</p>
                  <p className="text-xs text-amber-700 mt-0.5">Added before each course had its own times. Edit each one to pick its course, or delete it.</p>
                </div>
                <div className="bg-white">
                  <SlotTable slots={noCourse} {...tableProps} />
                </div>
              </div>
            )}

            {SLOT_COURSES.map(c => {
              const list = slotsFor(c.value);
              const open = list.filter(s => s.available).length;
              return (
                <div key={c.value} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-bold text-gray-800">{c.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {list.length === 0 ? 'No times yet' : `${open} of ${list.length} shown to students`}
                      </p>
                    </div>
                    <button onClick={() => setModal({ slot: null, course: c.value })}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold text-white whitespace-nowrap"
                      style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
                      + Add time
                    </button>
                  </div>
                  {list.length === 0 ? (
                    <p className="px-5 pb-5 text-xs text-gray-400">
                      Students who pick {c.label} are told the team will contact them to schedule.
                    </p>
                  ) : (
                    <SlotTable slots={list} {...tableProps} />
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {modal && (
        <SlotModal
          slot={modal.slot}
          initialCourse={modal.course}
          adminPassword={adminPassword}
          nextOrder={nextOrder}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
    </TrackerLayout>
  );
};

export default SessionSlots;
